import {
  IconCounterConversionPath,
  IconCounterRecycling,
  IconCounterStarShine,
} from "assets/svg";
import React from "react";
import { GlobalCounterData } from "reducers/rankingApi";
import {
  formatWeightUnit,
  formatWeightValue,
} from "common/helper/quantity.helper";

interface GlobalCounterSectionProps {
  counter: GlobalCounterData | null;
}

const formatCount = (value: number) => value.toLocaleString("en-US");
const formatCounterValue = (value: number | null | undefined) =>
  value && value > 0 ? formatCount(value) : "--";
const formatCounterWeightValue = (value: number | null | undefined) =>
  value && value > 0 ? formatWeightValue(value) : "--";
const formatCounterWeightUnit = (value: number | null | undefined) =>
  value && value > 0 ? formatWeightUnit(value) : "Kg";

const MetricValue = ({ value, unit }: { value: string; unit?: string }) => (
  <div className="flex shrink-0 items-end justify-center gap-1 text-[#d8ff36]">
    <span className="font-['Audiowide'] text-[30px] leading-none md:text-[36px]">
      {value}
    </span>
    {unit && (
      <span className="font-['Audiowide'] text-[16px] leading-none">
        {unit}
      </span>
    )}
  </div>
);

const GlobalCounterSection = ({ counter }: GlobalCounterSectionProps) => {
  const totalCirculationKg = counter?.total_circulation_kg ?? null;

  return (
    <div className="grid w-full gap-2.5 md:gap-5 xl:grid-cols-[minmax(0,1.75fr)_minmax(17.5rem,1fr)]">
      <div className="rounded-md border border-[#002959] bg-[linear-gradient(154.13deg,#1B4B86_0%,#002959_99.28%)] px-5 py-6 text-white shadow-md md:px-9">
        <h3 className="mb-4 text-center text-lg font-bold md:mb-[1.625rem] md:text-xl">
          全体の社会貢献カウンター
        </h3>
        <div className="grid md:grid-cols-3">
          <div className="flex items-center justify-between border-b border-white/60 py-3 md:flex-col md:justify-start md:gap-3 md:border-b-0 md:border-r md:py-0">
            <div className="flex items-center gap-1.5 text-sm font-bold">
              <IconCounterRecycling className="h-[1.375rem] w-[1.375rem]" />
              総循環量
            </div>
            <MetricValue
              value={formatCounterWeightValue(totalCirculationKg)}
              unit={formatCounterWeightUnit(totalCirculationKg)}
            />
          </div>
          <div className="flex items-center justify-between border-b border-white/60 py-3 md:flex-col md:justify-start md:gap-3 md:border-b-0 md:border-r md:py-0">
            <div className="flex items-center gap-1.5 text-sm font-bold">
              <IconCounterStarShine className="h-[1.375rem] w-[1.375rem]" />
              総出品数
            </div>
            <MetricValue value={formatCounterValue(counter?.products_on_sale)} />
          </div>
          <div className="flex items-center justify-between pt-3 md:flex-col md:justify-start md:gap-3 md:pt-0">
            <div className="flex items-center gap-1.5 text-sm font-bold">
              <IconCounterConversionPath className="h-[1.375rem] w-[1.375rem]" />
              総成約件数
            </div>
            <MetricValue value={formatCounterValue(counter?.completed_deals)} />
          </div>
        </div>
      </div>

      <div className="grid gap-2.5">
        {[
          [
            "参加企業数",
            formatCounterValue(counter?.participating_companies),
          ],
          ["本日取引件数", formatCounterValue(counter?.deals_today)],
        ].map(([label, value]) => (
          <div
            key={label}
            className="flex min-h-16 items-center justify-between rounded-md border border-[#002959] bg-[linear-gradient(154.13deg,#1B4B86_0%,#002959_99.28%)] px-5 py-3 text-white shadow-md md:min-h-[5.25rem] md:px-[1.875rem] md:py-5"
          >
            <span className="text-base font-bold">{label}</span>
            <span className="font-['Audiowide'] text-[1.875rem] leading-none text-[#d8ff36] md:text-[2.125rem]">
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GlobalCounterSection;
